/**
 * taskClosure — two-person closure for the `tasks` collection.
 *
 *   assignee  → submitTaskForReview()  status 'pending_review' + note / files + named reviewer
 *   reviewer  → approveTask()          status 'completed'  (never the submitter)
 *             → returnTask()           status 'in_progress' with a reason
 *
 * firestore.rules (taskClosureOk) enforces the same steps, so a board that
 * writes status:'completed' directly is refused. Boards call setTaskStatus(),
 * which routes "completed" to the completion popup hosted by TaskHost.
 */
import { getApp } from 'firebase/app';
import { getFirestore, doc, updateDoc, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { actorStamp } from './actorStamp';

export const REVIEW_STATUS = 'pending_review';

const db = () => getFirestore(getApp());

/** Legacy 3-column boards: in-review tasks sit in IN PROGRESS. */
export const boardStatus = s => (s === REVIEW_STATUS ? 'in_progress' : s);

export const STATUS_LABEL = {
  pending: 'Pending', in_progress: 'In progress', [REVIEW_STATUS]: 'In review',
  completed: 'Completed', blocked: 'Blocked',
};

export const assigneesOf = t => [...new Set([...(t?.assigneeIds || []), t?.assigneeId, t?.assignedTo]
  .filter(x => typeof x === 'string' && x))];

/** Opens the "submit completion for review" popup (TaskHost). */
export function requestTaskCompletion(taskId) {
  if (taskId) window.dispatchEvent(new CustomEvent('smc:completeTask', { detail: { taskId } }));
}

/** Opens the task popup (TaskHost) — details, review, approve / return. */
export function openTask(taskId) {
  if (taskId) window.dispatchEvent(new CustomEvent('smc:openTask', { detail: { taskId } }));
}

/** Status change from a board. Closing always goes through review. */
export async function setTaskStatus(taskId, status) {
  if (status === 'completed') { requestTaskCompletion(taskId); return; }
  await updateDoc(doc(db(), 'tasks', taskId), { ...actorStamp(), status, updatedAt: serverTimestamp() });
}

function historyEntry(action, user, note = '') {
  return { action, byUid: user.uid, byName: user.name || '', at: new Date().toISOString(), note };
}

export async function submitTaskForReview(task, { note = '', attachments = [], reviewer, user }) {
  if (!reviewer?.uid || reviewer.uid === user.uid) throw new Error('Pick someone else to review this task.');
  await updateDoc(doc(db(), 'tasks', task.id), {
    ...actorStamp(),
    status: REVIEW_STATUS,
    completionNote: note.trim(),
    completionAttachments: attachments,
    review: {
      submittedByUid: user.uid, submittedByName: user.name || '',
      submittedAt: new Date().toISOString(),
      reviewerUid: reviewer.uid, reviewerName: reviewer.name || '',
    },
    reviewHistory: arrayUnion(historyEntry('submitted', user, note.trim())),
    updatedAt: serverTimestamp(),
  });
}

export async function approveTask(task, { note = '', user }) {
  const review = task.review || {};
  await updateDoc(doc(db(), 'tasks', task.id), {
    ...actorStamp(),
    status: 'completed',
    approvedByUid: user.uid, approvedByName: user.name || '',
    approvedAt: serverTimestamp(), approvalNote: note.trim(),
    completedByUid: review.submittedByUid || null, completedByName: review.submittedByName || '',
    completedAt: serverTimestamp(),
    reviewHistory: arrayUnion(historyEntry('approved', user, note.trim())),
    updatedAt: serverTimestamp(),
  });
  // Mission Log @-tag tasks: the notifyTaskWrite trigger mirrors the closure
  // onto daily_updates/{id}/taggedTasks/{submitter} (rules block the reviewer).
}

export async function returnTask(task, { note, user }) {
  if (!note?.trim()) throw new Error('Say what is missing so they can fix it.');
  await updateDoc(doc(db(), 'tasks', task.id), {
    ...actorStamp(),
    status: 'in_progress',
    reviewReturnNote: note.trim(),
    reviewReturnedAt: serverTimestamp(),
    reviewReturnedByUid: user.uid, reviewReturnedByName: user.name || '',
    reviewHistory: arrayUnion(historyEntry('returned', user, note.trim())),
    updatedAt: serverTimestamp(),
  });
}

/** Submitter takes the task back out of review. */
export async function withdrawReview(task, { user }) {
  await updateDoc(doc(db(), 'tasks', task.id), {
    ...actorStamp(),
    status: 'in_progress',
    reviewHistory: arrayUnion(historyEntry('withdrawn', user)),
    updatedAt: serverTimestamp(),
  });
}
